import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card';
import {
  resolveDeliverableKind,
  resolveReportUrl,
} from '@/lib/chatDeliverable';
import {
  fetchReportHtmlContent,
  openReportHtmlInNewTab,
  resolveReportFetchUrl,
} from '@/lib/reportHtml';
import IntegrationSuitePanelBlocks, {
  integrationSuiteCopyPayload,
} from '@/components/IntegrationSuitePanelBlocks';
import ReportHtmlFrame from '@/components/ReportHtmlFrame';
import WorkflowTaskGraph from '@/components/WorkflowTaskGraph';
import { getWorkflowStageLabel, getWorkflowSteps } from '@/lib/workflowHelpers';
import { sanitizeRailArtifactSummary } from '@/lib/chatDisplay';
import { cn } from '@/lib/utils';
import { permissionDeniedTitle } from '@/lib/permissionUi';
import { useEffect, useState } from 'react';
import {
  Check,
  Code2,
  Copy,
  ExternalLink,
  FileText,
  LayoutPanelLeft,
  Loader2,
  MessageSquare,
  Rocket,
  UserPlus,
  Workflow,
} from 'lucide-react';

const PANEL_META = {
  workspace: {
    title: 'Workspace',
    description: 'Code, reports, diagrams, and tool output appear here',
    icon: LayoutPanelLeft,
    emptyTitle: 'Nothing to show yet',
    emptyHint:
      'Ask for ABAP, security changes, reports, workflows, or Integration Suite tasks in chat. Output for the active turn shows up here.',
    missingTitle: 'No visual output for this turn',
    missingHint: 'The reply may be chat-only. Run a task that produces code, HTML, or structured results.',
  },
  abap: {
    title: 'Generated ABAP',
    description: 'Your generated SAP ABAP program',
    icon: Code2,
    emptyTitle: 'No ABAP output yet',
    emptyHint: 'After you approve the requirements and generation completes, your program appears here.',
    missingTitle: 'Generation finished but no ABAP was returned',
    missingHint: 'Try refreshing the page or start a new chat. Check backend logs if this persists.',
  },
  security: {
    title: 'Security results',
    description: 'Summary from the Security Agent',
    icon: MessageSquare,
    emptyTitle: 'No results yet',
    emptyHint: 'After you approve the security request, results appear here.',
    missingTitle: 'Security Agent finished but no summary was returned',
    missingHint: 'Say yes or approve after reviewing the security request, or start a new chat.',
  },
  static_report: {
    title: 'Static report',
    description: 'HTML analytics report from SAP data',
    icon: FileText,
    emptyTitle: 'No report yet',
    emptyHint: 'After you approve requirements, the report link appears here.',
    missingTitle: 'Report finished but no link or preview was returned',
    missingHint: 'The report may have failed server-side — check logs or retry with Connected mode on.',
  },
  integration_suite: {
    title: 'Integration Suite',
    description: 'Live output from BTP Integration Suite tools',
    icon: Workflow,
    emptyTitle: 'No Integration Suite output yet',
    emptyHint:
      'Ask about iFlows, packages, API proxies, or mappings in chat. Connected mode and MCP binding required.',
    missingTitle: 'Agent finished but no panel content was returned',
    missingHint: 'Try another prompt or check Integration Suite MCP logs on the server.',
  },
  workflow: {
    title: 'New User Provisioning',
    description: 'Workflow progress, roles, SoD, and approval',
    icon: UserPlus,
    emptyTitle: 'No workflow results yet',
    emptyHint:
      'Describe a New User Provisioning request in chat (User, Email, Reference user). Connected mode required.',
    missingTitle: 'Workflow finished but no summary was returned',
    missingHint: 'Reply with missing fields, or type approve / reject when prompted.',
  },
};

function StaticReportPreview({ reportUrl, onCopyUrl }) {
  const [html, setHtml] = useState('');
  const [fetchUrl, setFetchUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!reportUrl) {
      setHtml('');
      setFetchUrl('');
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);
    fetchReportHtmlContent(reportUrl)
      .then(({ html: content, fetchUrl: resolved }) => {
        if (cancelled) return;
        setHtml(content);
        setFetchUrl(resolved || resolveReportFetchUrl(reportUrl) || reportUrl);
        onCopyUrl?.(resolved || resolveReportFetchUrl(reportUrl) || reportUrl);
      })
      .catch((err) => {
        if (cancelled) return;
        setHtml('');
        setFetchUrl(resolveReportFetchUrl(reportUrl) || reportUrl);
        setError(err?.message || 'Failed to load report');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reportUrl, onCopyUrl]);

  const displayUrl = fetchUrl || resolveReportFetchUrl(reportUrl) || reportUrl;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-cp-line bg-cp-inset p-4">
        <div className="mb-2 text-xs font-medium text-muted-foreground">Report URL</div>
        <div className="flex items-start gap-2 break-all text-sm text-cp-indigo">
          <FileText className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{displayUrl}</span>
        </div>
      </div>
      {loading && (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading report…
        </div>
      )}
      {error && !loading && (
        <div className="rounded-lg border border-cp-mod bg-cp-inset p-3 text-sm text-cp-mod">
          {error}
        </div>
      )}
      {!loading && html && (
        <>
          <Button
            type="button"
            className="w-full"
            onClick={() => {
              try {
                openReportHtmlInNewTab(html);
              } catch (err) {
                setError(err?.message || 'Could not open report');
              }
            }}
            data-testid="chat-open-static-report"
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Open report
          </Button>
          <div
            className={cn(
              'overflow-hidden rounded-lg border border-cp-line bg-cp-surface',
              'max-h-[min(360px,45vh)]'
            )}
          >
            <ReportHtmlFrame
              html={html}
              title="Static report preview"
              className="h-[min(360px,45vh)] w-full border-0"
            />
          </div>
        </>
      )}
    </div>
  );
}

export default function ChatDeliverablePanel({
  intent = '',
  artifactType = null,
  generatedCode = null,
  artifactSummary = null,
  panelBlocks = null,
  artifactUrl = null,
  workflowStage = null,
  workflowState = null,
  chatAtDone = false,
  /** Panel shows prior deliverable while a new turn is running. */
  deliverableUpdating = false,
  showPanel = false,
  deployAvailable = false,
  deploying = false,
  deployMessage = null,
  canDeploy = true,
  onDeploy,
  onCopy,
  copied = false,
  showValidationHint = false,
  /** When true, skip the graph (e.g. already shown in the chat header). */
  hideWorkflowGraph = false,
  /** Fill parent height (Studio right rail) so the code box uses remaining space. */
  fillHeight = false,
}) {
  const kind = resolveDeliverableKind(intent, artifactType, generatedCode);
  const meta = PANEL_META[kind] || PANEL_META.workspace;
  const Icon = meta.icon;
  const code = String(generatedCode || '').trim();
  const blocks = Array.isArray(panelBlocks) ? panelBlocks : [];
  const summary = String(
    sanitizeRailArtifactSummary(artifactSummary, { intent, artifactType }) ||
      (kind === 'security' || kind === 'workflow' || kind === 'integration_suite'
        ? sanitizeRailArtifactSummary(generatedCode, { intent, artifactType })
        : '') ||
      ''
  ).trim();
  const reportUrl = kind === 'static_report' ? resolveReportUrl(artifactUrl, generatedCode) : '';
  const [copyTarget, setCopyTarget] = useState('');
  const wfTicket = { workflow_stage: workflowStage, workflow_state: workflowState || {} };
  const wfSteps = kind === 'workflow' ? getWorkflowSteps(wfTicket) : [];
  const stageLabel =
    kind === 'workflow' ? getWorkflowStageLabel(wfTicket) : null;

  const hasContent =
    kind === 'static_report'
      ? Boolean(reportUrl || code)
      : kind === 'workflow'
        ? Boolean(summary || wfSteps.length > 0)
        : kind === 'integration_suite'
          ? Boolean(blocks.length > 0 || summary)
          : kind === 'security'
            ? Boolean(summary)
            : Boolean(code);

  const bodyScrollClass = fillHeight
    ? 'min-h-[12rem] flex-1 overflow-auto'
    : 'max-h-[min(420px,55vh)] overflow-auto';

  return (
    <Card
      className={cn(
        'border-cp-line shadow-md',
        fillHeight ? 'flex h-full min-h-0 flex-col' : 'xl:sticky xl:top-4 xl:self-start'
      )}
    >
      <div className="shrink-0 rounded-t-xl border-b border-cp-line bg-cp-surface px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="rounded-md border border-cp-line bg-cp-surface p-1.5 shadow-sm">
            <Icon className="h-4 w-4 text-cp-muted" />
          </div>
          <div>
            <CardTitle className="text-base">{meta.title}</CardTitle>
            <CardDescription className="text-xs text-cp-muted">{meta.description}</CardDescription>
          </div>
        </div>
      </div>
      <CardContent
        className={cn('p-4', fillHeight && 'flex min-h-0 flex-1 flex-col')}
      >
        {showPanel && hasContent ? (
          <>
            <div className="mb-3 flex shrink-0 flex-wrap items-center justify-between gap-2">
              {chatAtDone ? (
                <Badge className="border-cp-ok bg-cp-inset text-cp-ok">Ready</Badge>
              ) : (
                <Badge variant="secondary" className="text-xs">
                  {deliverableUpdating ? 'Updating…' : 'In progress'}
                </Badge>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {kind === 'abap' && deployAvailable && (
                  <span title={permissionDeniedTitle(canDeploy, 'deploy chat code')}>
                    <Button
                      type="button"
                      size="sm"
                      className="bg-gradient-to-r from-cp-ok to-cp-teal text-white hover:opacity-95"
                      onClick={onDeploy}
                      disabled={!canDeploy || deploying || !code}
                      data-testid="chat-deploy-sap"
                    >
                      {deploying ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Deploying…
                        </>
                      ) : (
                        <>
                          <Rocket className="mr-2 h-4 w-4" />
                          Deploy to SAP
                        </>
                      )}
                    </Button>
                  </span>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-cp-line"
                  onClick={onCopy}
                  disabled={
                    kind === 'static_report'
                      ? !copyTarget && !reportUrl && !code
                      : kind === 'integration_suite'
                        ? !integrationSuiteCopyPayload(blocks, summary)
                        : kind === 'security' || kind === 'workflow'
                          ? !summary
                          : !code
                  }
                >
                  {copied ? (
                    <>
                      <Check className="mr-2 h-4 w-4 text-cp-ok-600" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="mr-2 h-4 w-4" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
            </div>
            <div
              className={cn(
                'flex min-h-0 flex-col',
                deliverableUpdating && 'pointer-events-none opacity-60'
              )}
            >
            {deployMessage && kind === 'abap' && (
              <p className="mb-3 shrink-0 text-xs text-cp-ok">{deployMessage}</p>
            )}
            {kind === 'abap' && showValidationHint && (
              <p className="mb-3 shrink-0 text-xs text-cp-mod">
                Deploy to SAP requires Connected mode and a passing SAP syntax check. If validation
                failed, revise the program or start a new chat with Connected mode on.
              </p>
            )}

            {kind === 'abap' && (
              <div
                className={cn(
                  bodyScrollClass,
                  // Console bg is always dark; force light code text so light theme stays readable.
                  'rounded-lg border border-cp-line bg-cp-console p-4 font-mono text-xs leading-relaxed text-[#e8edf7] shadow-inner ring-1 ring-cp-line'
                )}
              >
                <pre className="whitespace-pre-wrap break-words text-[#e8edf7]">{code || '—'}</pre>
              </div>
            )}

            {kind === 'security' && (
              <div
                className={cn(
                  bodyScrollClass,
                  'rounded-lg border border-cp-line bg-cp-inset p-4 text-sm leading-relaxed text-cp-fg shadow-inner'
                )}
              >
                <div className="whitespace-pre-wrap break-words">{summary}</div>
              </div>
            )}

            {kind === 'workflow' && (
              <div className={cn('space-y-4', fillHeight && 'flex min-h-0 flex-1 flex-col')}>
                {stageLabel && (
                  <Badge className="shrink-0 border-cp-mod bg-cp-inset text-cp-mod">{stageLabel}</Badge>
                )}
                {!hideWorkflowGraph && wfSteps.length > 0 && (
                  <WorkflowTaskGraph
                    steps={wfSteps}
                    currentStep={(workflowState || {}).current_step}
                  />
                )}
                {summary && (
                  <div
                    className={cn(
                      fillHeight
                        ? 'min-h-[8rem] flex-1 overflow-auto'
                        : 'max-h-[min(320px,40vh)] overflow-auto',
                      'rounded-lg border border-cp-line bg-cp-inset p-4 text-sm leading-relaxed text-cp-fg shadow-inner'
                    )}
                  >
                    <div className="whitespace-pre-wrap break-words">{summary}</div>
                  </div>
                )}
              </div>
            )}

            {kind === 'static_report' && (
              reportUrl ? (
                <StaticReportPreview reportUrl={reportUrl} onCopyUrl={setCopyTarget} />
              ) : (
                <div className="rounded-lg border border-cp-line bg-cp-inset p-4 text-sm text-cp-muted whitespace-pre-wrap break-words">
                  {code}
                </div>
              )
            )}

            {kind === 'integration_suite' && (
              <IntegrationSuitePanelBlocks
                blocks={blocks}
                summary={summary}
                fillHeight={fillHeight}
              />
            )}
            </div>
          </>
        ) : (
          <div className="rounded-lg border border-dashed border-cp-line bg-cp-inset py-12 text-center">
            <Icon className="mx-auto mb-3 h-10 w-10 text-cp-muted" />
            <p className="text-sm text-muted-foreground">
              {chatAtDone && showPanel ? meta.missingTitle : meta.emptyTitle}
            </p>
            <p className="mt-1 px-4 text-xs text-muted-foreground">
              {chatAtDone && showPanel ? meta.missingHint : meta.emptyHint}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
